import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { CustomButton, CustomSelect, Text } from '../../../../shared/uiComponents';
import { PRIMARY, TEXT as TEXT_COLORS } from '../../../../shared/Helpers/colors';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { DEFAULT_TIMEZONE_ID, getWallClockTimeZoneAbbreviation, parseWallClockTime } from '../../../../shared/Helpers/timezones';
import { InfoFilled } from '../../../../shared/Assets/svgIcons';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { createSingleSessionCancellation } from '../../../../redux/State/clientSlice/scheduleCancellationSlice';
import {
	CancellationReason,
	CancellingParty,
	RescheduleIntent,
	clientReasonOptions,
	providerReasonOptions,
	rescheduleIntentOptions,
} from '../../../../redux/API/ClientAPIHelpers/scheduleCancellation';
import { EventDataProperties } from '../../../../redux/API/ClientAPIHelpers/calendarProperties';
import CancellationFormFields, { useCancellationForm } from '../../../../components/CancellationFormFields/useCancellationForm';

interface CancelSessionModalProps {
	data: EventDataProperties;
	onClose: () => void;
	onSuccess: () => void;
}

const formatDuration = (startTime: string, endTime: string): string => {
	const start = parseWallClockTime(startTime);
	const end = parseWallClockTime(endTime);
	const hours = end.diff(start, 'hour', true);
	const rounded = Math.round(hours * 100) / 100;

	return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
};

const isOtherReasonCheck = (reasonId: string) =>
	Number(reasonId) === CancellationReason.ProviderOther || Number(reasonId) === CancellationReason.ClientOther;

const cancellingPartyOptions = [
	{
		id: `${CancellingParty.Provider}`,
		label: 'I am canceling (BT)',
		description: 'You are unable to attend this session',
	},
	{
		id: `${CancellingParty.ClientFamily}`,
		label: 'Client/Family is canceling',
		description: 'The client or parent initiated the cancellation',
	},
	{
		id: `${CancellingParty.Agency}`,
		label: 'Agency is canceling',
		description: 'The agency initiated the cancellation',
	},
];

const CancelSessionModal: FC<CancelSessionModalProps> = ({ data, onClose, onSuccess }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const submitting = useSelector((state) => state.scheduleCancellation.submitting);
	const [rescheduleIntent, setRescheduleIntent] = useState<string>('');
	const tzSuffix = data.client.timeZoneId && data.client.timeZoneId !== DEFAULT_TIMEZONE_ID
		? ` (${getWallClockTimeZoneAbbreviation(data.client.timeZoneId, data.startTime)})`
		: '';

	const {
		cancellingParty,
		handleCancellingPartyChange,
		isAgency,
		isFormValid,
		isOtherReason,
		notes,
		notesRequired,
		reasonId,
		setNotes,
		setReasonId,
		setSubmitted,
		submitted,
	} = useCancellationForm(isOtherReasonCheck);

	const handleSubmit = () => {
		setSubmitted(true);
		if (!isFormValid || cancellingParty === null || !data.recurringScheduleId) return;

		dispatch(
			createSingleSessionCancellation({
				recurringScheduleId: data.recurringScheduleId,
				recurringScheduleTimeSlotId: data.recurringScheduleTimeSlotId,
				date: data.date,
				cancellingParty,
				reasonId: isAgency ? undefined : Number(reasonId),
				rescheduleIntent: rescheduleIntent === '' ? undefined : (Number(rescheduleIntent) as RescheduleIntent),
				notes: notes.trim() || undefined,
			}),
		)
			.then(unwrapResult)
			.then(() => {
				toast('Session cancelled');
				onSuccess();
			})
			.catch(errorNormalizer);
	};

	const filteredReasons = cancellingParty === CancellingParty.Provider
		? providerReasonOptions
		: cancellingParty === CancellingParty.ClientFamily
			? clientReasonOptions
			: [];
	const reasonOptions = filteredReasons.map((r) => ({ id: `${r.id}`, label: r.label }));
	const rescheduleOptions = rescheduleIntentOptions.map((r) => ({ id: `${r.id}`, label: r.label }));

	return (
		<div className="cancelSessionModal">
			<div className="cancelSessionDetails">
				<Text title="SESSION DETAILS" type="pxs" fontWeight="semiBold" color={TEXT_COLORS.TERTIARY} />
				<Text title={dayjs(data.date).format('dddd, MMMM D, YYYY')} type="psm" fontWeight="semiBold" />
				<Text
					title={`${parseWallClockTime(data.startTime).format('h:mm A')} - ${parseWallClockTime(data.endTime).format('h:mm A')}${tzSuffix} (${formatDuration(data.startTime, data.endTime)})`}
					type="pxs"
					color={TEXT_COLORS.SECONDARY}
				/>
				<Text
					title={`${data.client.fullName} \u2022 ${data.sessionType.name}`}
					type="pxs"
					color={TEXT_COLORS.SECONDARY}
				/>
			</div>

			<div className="cancelSessionInfoBanner">
				<InfoFilled color={PRIMARY.BASE} />
				<div>
					<Text title="Cancellation Request" type="psm" fontWeight="semiBold" />
					<Text
						title="This cancellation will apply immediately. Your Case Manager and BCBA will be notified."
						type="pxs"
						color={TEXT_COLORS.SECONDARY}
					/>
				</div>
			</div>

			<CancellationFormFields
				beforeNotes={
					<div className="cancelSessionField">
						<div className="fieldLabelRow">
							<Text title="Will you reschedule this session?" type="psm" fontWeight="semiBold" />
						</div>
						<CustomSelect
							className="cancelSessionSelect"
							placeholder="Select..."
							data={rescheduleOptions}
							value={rescheduleIntent}
							setValue={(e) => setRescheduleIntent(e.target.value)}
						/>
						<Text
							title={
								Number(rescheduleIntent) === RescheduleIntent.Yes
									? 'Please coordinate with the parent and create a new session note for the make-up session.'
									: 'If rescheduling, coordinate with the client and update your Case Manager'
							}
							type="pxs"
							color={TEXT_COLORS.TERTIARY}
						/>
					</div>
				}
				cancellingParty={cancellingParty}
				cancellingPartyOptions={cancellingPartyOptions}
				className="cancelSessionField"
				inputClassName="cancelSessionNotes"
				isAgency={isAgency}
				isOtherReason={isOtherReason}
				notes={notes}
				notesRequired={notesRequired}
				onCancellingPartyChange={handleCancellingPartyChange}
				onNotesChange={setNotes}
				onReasonChange={setReasonId}
				reasonId={reasonId}
				reasonOptions={reasonOptions}
				selectClassName="cancelSessionSelect"
				submitted={submitted}
			/>

			<div className="cancelSessionActions">
				<CustomButton
					title="Go Back"
					buttonType="tertiary"
					onClick={onClose}
					className="cancelActionButton"
					sizeType="xlg"
				/>
				<CustomButton
					title="Cancel Session"
					buttonType="primary"
					onClick={handleSubmit}
					loading={submitting}
					className="confirmCancelActionButton"
					sizeType="xlg"
					style={{ backgroundColor: '#ef4444' }}
				/>
			</div>
		</div>
	);
};

export default CancelSessionModal;
